import axios from 'axios';
import fileDownload from 'js-file-download';
import _, { throttle } from 'lodash';
import moment from 'moment';
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ForwardedRef,
} from 'react';
import { clearTimeout, setTimeout } from 'worker-timers';
import {
  fetchAIProcessingStatus,
  handleCopyAiReport,
  handleDownloadPdfReport,
  handleGenerateHolterReport,
  handleGenerateHolterReportComments as handleGenerateHolterReportComment,
  handleUpdateSummaryReport,
  handleUploadAiReport,
  selectIsActiveTab,
  useReportStore,
} from '@/entities/report';
import { fetchHolterTasks } from '@/entities/study';
import { handleUpdateFacilityNote } from '@/entities/facility';
import { clearApolloCache } from '@/shared/api';
import {
  EMITTER_CONSTANTS,
  LOADING_TYPES,
  REPORT_TYPES,
  TAB_KEY,
  URL_CALLCENTER_PORTAL,
  CALL_CENTER_API,
} from '@/shared/config';
import {
  FACILITY_NOTES_MAX_CHAR,
  dataToStringForNameReport,
  getAccessToken,
  getLineParagraph,
  removeEmptyLineBreak,
  uploadFileToUrl,
  zeroPad,
} from '@/shared/lib';
import { checkLimitStudyProcessing, isHolterProcessingAvailable } from '@/shared/lib/aiUtils';
import { useEmitter, useMergeState, useUpdateEffect } from '@/shared/lib/customHooks';
import { useLoadingStore } from '@/shared/lib/loadingStore';
import { toastrError, toastrInfo, toastrSuccess } from '@/shared/lib/toastNotification';
import { logError } from '@/shared/lib/apiHelpers';
import { useUserStore } from '@/entities/user';
import { useShallow } from 'zustand/react/shallow';
import { ReportSettingsRef } from './ReportSettings';
import { CHARACTER_PER_LINE, MAX_LINE_COUNT } from './constants';
import type { AiPDFReportTabProps, AiPDFReportTabRef } from './index';

const TIMEOUT_RENDER_REPORT = 900000;

/**
 * Drives the AI PDF report tab: report data and findings state, PDF download, report generation, copy and upload flows. Reads report, findings and facility note from the report store, downloads the rendered PDF over axios with throttled progress and estimate updates, guards findings and facility note length, tracks rendering timeouts with a timer, listens to AI processing and report update emitter events, and exposes handlers plus derived disabled flags.
 *
 * @param props - Tab props carrying the active tab id, the read-only and aborted study flags and the call center generating flag.
 * @param ref - Forwarded ref receiving the tab's imperative handle with isChangedData, onClickSaveReport and resetData.
 * @returns An object carrying the report data, findings and facility note, local state, action handlers and the derived disabled flags for the tab UI.
 */
const useAiPDFReportTab = (
  {
    activeTab,
    isAbortedStudy,
    isNotEdit,
    isGeneratingNormalReportInCallCenter,
  }: AiPDFReportTabProps,
  ref: ForwardedRef<AiPDFReportTabRef>,
) => {
  const keyRecoil = 'REPORT';

  const loadingPageAction = useLoadingStore((s) => s.loadingPage);

  const reportData = useReportStore((s) => s.reportData);
  const setReportData = useReportStore((s) => s.actions.setReportData);
  const findings = useReportStore((s) => s.findings);
  const setFindings = useReportStore((s) => s.actions.setFindings);
  const setFindingChanged = useReportStore((s) => s.actions.setFindingsChange);
  const facilityNote = useReportStore((s) => s.facilityNote);
  const setFacilityNote = useReportStore((s) => s.actions.setFacilityNote);
  const setIsOpenProcessingLimit = useReportStore((s) => s.actions.setIsOpenProcessingLimit);
  const setProgressDownloadState = useReportStore((s) => s.actions.setProgressDownload);
  const setEstimateDownloadState = useReportStore((s) => s.actions.setEstimateDownload);
  const setIsDownloadingPdfReport = useReportStore((s) => s.actions.setIsDownloadingPdfReport);
  const isShowTabHeader = useReportStore((s) => s.isShowTabHeader);
  const setLimitProcessingStudy = useReportStore((s) => s.actions.setLimitProcessingStudy);
  // reportInfoState (Recoil, Task 7) is typed {} | IReportInfo (atom default is {}) - pre-existing gap.
  const { studyFid, studyId, profileId, reportType, reportFid, facilityId } = useReportStore(
    (s) => s.reportInfo,
  ) as any;
  const [isFailedDownloadReport, setFailedDownloadReport] = useState(false);

  const [state, setState] = useMergeState<{
    isOpenCopyReportModal: boolean;
    isSaving: boolean;
    isDownloadingPdfReport: boolean;
    pdfReport: {
      url: string;
      objectUrl: string;
      blobData?: Blob;
      fileName?: string;
    };
    isOpenTimeoutReportModal: boolean;
    isOpenUpdateFindingsModal?: boolean;
  }>({
    isOpenCopyReportModal: false,
    isSaving: false,
    isDownloadingPdfReport: false,
    pdfReport: {
      url: '',
      objectUrl: '',
    },
    isOpenTimeoutReportModal: false,
  });

  const isActiveTab = useReportStore(selectIsActiveTab(keyRecoil));
  const setIsActiveTabAction = useReportStore((s) => s.actions.setIsActiveTab);
  /**
   * Marks the PDF report tab as the active one in the report store. Forwards the value to the store action under the REPORT key held by keyRecoil.
   *
   * @param v - Next active-tab value or updater accepted by the store action.
   * @returns Nothing.
   */
  const setIsActiveTab = (v: Parameters<typeof setIsActiveTabAction>[1]) =>
    setIsActiveTabAction(keyRecoil, v);
  const [isTabActive, setIsTabActive] = useState(false);
  const [isFindingsDisabled, setIsFindingsDisabled] = useState(false);

  const hasVisitTab = useRef(false);
  const dropzoneRef = useRef<any>(null);
  const isGenerateFindings = useRef(false);
  const reportSettingRef = useRef<ReportSettingsRef>(null);
  const timerRenderReport = useRef<ReturnType<typeof setTimeout>>();

  const { roles = [], rolePermissions = {} } = useUserStore(
    useShallow((s) => ({
      roles: s.me?.roles,
      rolePermissions: s.me?.group?.rolePermissions,
    })),
  );

  const editFacilityNoteRoles = Array.isArray(rolePermissions?.editFacilityNotes)
    ? rolePermissions.editFacilityNotes
    : [];

  const canEditFacilityNote = !!_.intersection(roles, editFacilityNoteRoles)[0];
  const isFacilityNoteDisabled = isNotEdit || !canEditFacilityNote;

  /**
   * Applies edits to the facility note and findings fields under their length and line limits. A facility note is stored even when it exceeds the character cap, only raising a toast; findings are measured by wrapped line count, refused with a toast when over the maximum, and otherwise stored while flagging whether they differ from the report's technician comments.
   *
   * @param name - Field being edited, either facilityNote or findings.
   * @param value - New text entered for that field.
   * @returns Nothing.
   */
  const onChange = (name: string, value: string) => {
    if (name === 'facilityNote') {
      if (value.length > FACILITY_NOTES_MAX_CHAR) {
        toastrError(`Exceeded maximum of ${FACILITY_NOTES_MAX_CHAR} characters in Facility Notes`);
      }
      setFacilityNote(value);
    }
    if (name === 'findings') {
      const maxLineCount = MAX_LINE_COUNT;
      const characterPerLine = CHARACTER_PER_LINE;
      let lineCount = 0;
      const splitedLineBreakValue = value.split(/\r\n|\r|\n/);
      _.remove(splitedLineBreakValue, (x) => !x); // *: Remove empty lines
      lineCount += splitedLineBreakValue.length; // *: Line count included all line breaks
      _.forEach(splitedLineBreakValue, (splitedLine) => {
        const singleLineCount = Math.ceil(splitedLine.length / characterPerLine);
        if (singleLineCount > 1) {
          // *: Add more line count if each single line has larger than 1 line
          lineCount += singleLineCount - 1;
        }
      });
      if (lineCount > maxLineCount) {
        toastrError('Could not add more lines', 'Error');
      } else {
        setFindings(value);
        const currentFindings =
          reportData?.report?.technicianComments || reportData?.holterProfile?.technicianComments;
        setFindingChanged((value !== currentFindings) as any); // pre-existing bug: findingsChangeState is typed atom<String>, fed a boolean
      }
    }
  };

  /**
   * Builds the download file name for the current PDF report from the study, facility and patient details. Joins the report type or the word Summary, the facility name, the patient id, the zero-padded study friendly id, an optional zero-padded report id and the formatted study start date into a .pdf name.
   *
   * @returns The composed PDF file name for the current report.
   */
  const getReportFileName = () => {
    const { report, study } = reportData;
    const { facility } = study;
    const { patient } = study?.info || {};
    return `${report?.reportId ? report?.type : 'Summary'}-${facility?.name}-${patient?.patientId}-${zeroPad(study?.friendlyId)}${report?.reportId ? `-${zeroPad(report?.reportId)}` : ''}-${dataToStringForNameReport(study?.start, study?.timezone)}.pdf`;
  };

  /**
   * Pushes PDF download progress into the report store at most ten times a second. Wraps the store's progress setter in a lodash throttle with a 100 ms window and a leading call, memoized once for the lifetime of the tab.
   *
   * @returns The throttled progress setter.
   */
  const throttleSetProgressDownload = useCallback(
    throttle(
      (progress) => {
        setProgressDownloadState(progress);
      },
      100,
      { leading: true },
    ),
    [],
  );

  /**
   * Pushes the PDF download time estimate into the report store at most twice a second. Wraps the store's estimate setter in a lodash throttle with a 500 ms window and a leading call, memoized once for the lifetime of the tab.
   *
   * @returns The throttled estimate setter.
   */
  const throttleSetEstimateDownload = useCallback(
    throttle(
      (estimate) => {
        setEstimateDownloadState(estimate);
      },
      500,
      { leading: true },
    ),
    [],
  );

  /**
   * Fetches the rendered PDF report and turns it into a blob object URL for in-page viewing. Requests a signed download URL through handleDownloadPdfReport, pulls the bytes with axios as an array buffer using the access token and throttled progress callbacks, stores the blob, object URL and generated file name in state, and clears the downloading flags on every failure path before resetting progress and the page loader.
   *
   * @returns A promise resolving once the PDF has been stored in state or the download has failed.
   */
  const downloadPdfReport = async () => {
    try {
      setState({ isDownloadingPdfReport: true });
      setIsDownloadingPdfReport(true);
      const downloadPdfReportResult = await handleDownloadPdfReport({
        studyFid,
        reportType,
        reportId: reportFid,
      });
      const { data } = downloadPdfReportResult;
      const { downloadPdfReport } = data;
      if (downloadPdfReport.isSuccess) {
        try {
          const accessToken = await getAccessToken();
          const response = await axios.get(downloadPdfReport.url, {
            responseType: 'arraybuffer',
            headers: {
              'access-token': accessToken,
            },
            onDownloadProgress: (progressEvent) => {
              throttleSetProgressDownload(progressEvent.progress);
              throttleSetEstimateDownload(progressEvent.estimated);
            },
          });
          const blob = new Blob([response.data], { type: 'application/pdf' });
          const objectUrl = `${window.URL.createObjectURL(blob)}#view=FitW&zoom=100`;
          setState({
            pdfReport: {
              blobData: blob,
              url: downloadPdfReport.url,
              objectUrl,
              fileName: getReportFileName(),
            },
            isDownloadingPdfReport: false,
          });
          setIsDownloadingPdfReport(false);
        } catch (axiosError) {
          console.error('Failed to download PDF report (array buffer)', axiosError.message);
          setState({ isDownloadingPdfReport: false });
          setIsDownloadingPdfReport(false);
          setFailedDownloadReport(true);
        }
      } else {
        console.error('Failed to download PDF report, message: ', downloadPdfReport.message);
        setState({ isDownloadingPdfReport: false });
        setIsDownloadingPdfReport(false);
      }
    } catch (downloadPdfReportError) {
      console.error('Failed to download PDF report', downloadPdfReportError);
      setState({ isDownloadingPdfReport: false });
      setIsDownloadingPdfReport(false);
    }

    throttleSetProgressDownload(0);
    throttleSetEstimateDownload(undefined);
    loadingPageAction();
  };

  /**
   * Saves the already fetched PDF report to the user's disk. Passes the cached blob data and the generated file name to fileDownload, logging and toasting an error if the save throws.
   *
   * @returns A promise resolving once the download has been triggered or the failure toast shown.
   */
  const handleClickDownloadPdfReport = async () => {
    try {
      fileDownload(state.pdfReport?.blobData, state.pdfReport?.fileName);
    } catch (error) {
      logError('Failed to download PDF report: ', error);
      toastrError(error.message, 'Error');
    }
  };

  /**
   * Requests Holter report generation for the study, optionally saving a changed facility note alongside it. When retrying it only re-runs the PDF download; otherwise it validates the findings line count and note length, checks Holter task capacity, fires handleGenerateHolterReport and the facility note update in parallel, opens the processing limit modal when the study limit is hit, and mirrors the saved note into the cached report data.
   *
   * @param isRetryDownload - Whether the click retries a failed PDF download instead of requesting a new report.
   * @returns A promise resolving once generation has been requested or the flow has been rejected.
   */
  const handleGenerateReport = async (isRetryDownload?: boolean) => {
    if (isRetryDownload) {
      setFailedDownloadReport(false);
      downloadPdfReport();
      return;
    }
    const reportConfiguration = reportSettingRef.current?.getReportConfiguration();
    // pre-existing bug: findingsState/facilityNoteState are typed atom<String>, fed to string-only utils/APIs
    if (findings && getLineParagraph(findings as any) > MAX_LINE_COUNT) {
      toastrError(`Exceeded maximum of ${MAX_LINE_COUNT} lines in Findings`);
      return;
    }

    if (facilityNote && facilityNote.length > FACILITY_NOTES_MAX_CHAR) {
      toastrError(`Exceeded maximum of ${FACILITY_NOTES_MAX_CHAR} characters in Facility notes`);
      return;
    }

    try {
      loadingPageAction(LOADING_TYPES.GENERATE_REPORT);
      let isUpdateFacilityNote = false;
      const originalFacilityNote = reportData?.study?.facility?.facilityNote;
      if (facilityNote !== originalFacilityNote) {
        isUpdateFacilityNote = true;
      }
      const holterTasks = await fetchHolterTasks();
      if (!isHolterProcessingAvailable(holterTasks, reportData?.study?.friendlyId)) {
        setIsOpenProcessingLimit(true);
        loadingPageAction();
        return;
      }
      const promises: Promise<any>[] = [
        handleGenerateHolterReport({
          studyId,
          conclusion: removeEmptyLineBreak(findings as any),
          reportConfiguration,
          profileId,
        }),
      ];
      if (isUpdateFacilityNote) {
        promises.push(handleUpdateFacilityNote(facilityId, facilityNote as any));
      }
      const result = await Promise.all(promises);
      if (checkLimitStudyProcessing(result?.[0])) {
        setLimitProcessingStudy({
          isOpenModal: true,
          limitCount: result?.[0]?.simultaneousStudyLimit,
          studyFids: _.map(result?.[0]?.processingStudyFids, (x) => zeroPad(x)),
        });
        loadingPageAction();
        return;
      }

      if (isUpdateFacilityNote) {
        const reportDataClone = _.cloneDeep(reportData);
        _.assign(reportDataClone.study.facility, {
          facilityNote,
        });
        setReportData(reportDataClone);
      }

      setFindingChanged((prev) => {
        if (!prev) {
          return prev;
        }
        return '';
      });
      setIsFindingsDisabled(false);
    } catch (error) {
      logError('Failed to generate report: ', error);
      toastrError('Failed to generate report', error);
      loadingPageAction();
    }
  };

  /**
   * Retries Holter report generation after the rendering timeout modal has appeared. Collects the current report configuration from the settings ref, calls handleGenerateHolterReport with the study id, findings and profile id, then clears the saving and timeout modal flags, or on failure logs, toasts and stops the page loader.
   *
   * @returns A promise resolving once generation has been requested and the modal state settled.
   */
  const handleGenerateReportWhenError = async () => {
    setState({ isSaving: true });
    try {
      const reportConfiguration = reportSettingRef.current?.getReportConfiguration();
      await handleGenerateHolterReport({
        studyId,
        conclusion: findings,
        reportConfiguration,
        profileId,
      });
      setState({ isSaving: false, isOpenTimeoutReportModal: false });
    } catch (error) {
      logError('Failed to generate report: ', error);
      toastrError('Failed to generate report', error);
      loadingPageAction();
      setState({ isSaving: false });
    }
  };

  /**
   * Flips the visibility of the update findings confirmation modal. Writes the negated isOpenUpdateFindingsModal flag into the tab's merged state.
   *
   * @returns Nothing.
   */
  const toggleUpdateFindingsModal = () => {
    setState({ isOpenUpdateFindingsModal: !state.isOpenUpdateFindingsModal });
  };

  /**
   * Dismisses the modal shown when report rendering exceeded its timeout. Clears the isOpenTimeoutReportModal flag on the tab's merged state.
   *
   * @returns Nothing.
   */
  const hanldeCloseTimeoutReportModal = () => {
    setState({ isOpenTimeoutReportModal: false });
  };

  /**
   * Asks the backend to regenerate the report findings text for the study. Short-circuits with an info toast when findings are already disabled, otherwise disables them, shows the generating loader, closes the confirmation modal, checks Holter task capacity, clears the findings-changed flag and calls handleGenerateHolterReportComment, opening the processing limit modal when the simultaneous study limit is reached.
   *
   * @returns A promise resolving once findings regeneration has been requested or refused.
   */
  const handleUpdateFinding = async () => {
    if (isFindingsDisabled) {
      toastrInfo('The current findings cannot be updated because no values were changed.', '');
      setState({ isOpenUpdateFindingsModal: false });
      return;
    }
    setIsFindingsDisabled(true);
    try {
      loadingPageAction(`${LOADING_TYPES.GENERATE_FINDING} 0%`);
      toggleUpdateFindingsModal();
      const holterTasks = await fetchHolterTasks();
      if (!isHolterProcessingAvailable(holterTasks, studyFid)) {
        setIsOpenProcessingLimit(true);
        loadingPageAction();
        return;
      }
      setFindingChanged((prev) => {
        if (!prev) {
          return prev;
        }
        return '';
      });
      isGenerateFindings.current = true;
      const result = await handleGenerateHolterReportComment({
        studyId,
        profileId,
      });
      if (checkLimitStudyProcessing(result)) {
        setLimitProcessingStudy({
          isOpenModal: true,
          limitCount: result?.simultaneousStudyLimit,
          studyFids: _.map(result?.processingStudyFids, (x) => zeroPad(x as any)), // pre-existing bug: zeroPad(x) called with a string where it declares a number param
        });
        loadingPageAction();
      }
    } catch (error) {
      logError('Failed to update findings', error);
      toastrError('Failed to update findings', 'Error');
    }
  };

  /**
   * Opens a copy of the current report in the call center portal, either as an interim view or as a freshly duplicated study report. For an interim report it closes the modal and opens the portal URL built from the report start and stop epochs; otherwise it calls handleCopyAiReport with the study and report ids, toasts success and opens the study report page, or toasts the returned message on failure, toggling the saving flag around the call.
   *
   * @param isInterimReport - Whether the copy targets the interim report view instead of duplicating the AI report.
   * @returns A promise resolving once the portal window has been opened or the failure toast shown.
   */
  const handleCopyReport = async (isInterimReport?: boolean) => {
    if (isInterimReport) {
      if (_.isEmpty(reportData.report)) {
        return;
      }

      setState({ isOpenCopyReportModal: false });
      const startEpoch = moment(reportData.report.start).valueOf();
      const stopEpoch = moment(reportData.report.stop).valueOf();
      window.open(
        `${URL_CALLCENTER_PORTAL}studies/report/${studyFid}/interim/${startEpoch}/${stopEpoch}/${reportData.report?.id}`,
      );
      setState({ isOpenCopyReportModal: false });
      return;
    }

    setState({ isSaving: true });
    try {
      const result = await handleCopyAiReport({
        studyId,
        aiReportId: reportData.report?.id,
      });
      if (result?.isSuccess) {
        toastrSuccess('A new report was successfully copied.', '');
        setState({ isOpenCopyReportModal: false });
        window.open(`${URL_CALLCENTER_PORTAL}studies/report/${studyFid}/study`);
      } else {
        toastrError(result?.message, '');
      }
    } catch (error) {
      logError(error.message, error);
      toastrError(error.message, '');
    }
    setState({ isSaving: false });
  };

  /**
   * Persists edited findings and facility note for the report without regenerating it. Validates the findings line count and note length, compares both against the stored report values and returns early when nothing changed, then runs handleUpdateSummaryReport and handleUpdateFacilityNote as needed, mirrors the note into the cached report data and clears the findings-changed flag.
   *
   * @returns A promise resolving once the changed fields have been saved or the save was skipped.
   */
  const savePdfReportTab = async () => {
    // pre-existing bug: findingsState/facilityNoteState are typed atom<String>, fed to string-only utils/APIs
    if (findings && getLineParagraph(findings as any) > MAX_LINE_COUNT) {
      toastrError(`Exceeded maximum of ${MAX_LINE_COUNT} lines in Findings`);
      return;
    }

    if (facilityNote && facilityNote.length > FACILITY_NOTES_MAX_CHAR) {
      toastrError(`Exceeded maximum of ${FACILITY_NOTES_MAX_CHAR} characters in Facility notes`);
      return;
    }
    let isUpdateFindings = false;
    let isUpdateFacilityNote = false;
    const originalFindings =
      reportData?.report?.technicianComments || reportData?.holterProfile?.technicianComments;
    const originalFacilityNote = reportData?.study?.facility?.facilityNote;
    if (facilityNote !== originalFacilityNote) {
      isUpdateFacilityNote = true;
    }
    if (findings !== originalFindings) {
      isUpdateFindings = true;
    }
    if (!(isUpdateFindings || isUpdateFacilityNote)) {
      return;
    }
    try {
      loadingPageAction(LOADING_TYPES.SAVE_REPORT);
      const promise = [];
      if (isUpdateFindings) {
        promise.push(
          handleUpdateSummaryReport({
            studyId,
            technicianComments: findings,
            reportId: reportData?.report?.id,
          }),
        );
      }
      if (isUpdateFacilityNote) {
        promise.push(handleUpdateFacilityNote(facilityId, facilityNote as any));
      }
      await Promise.all(promise);
      if (isUpdateFacilityNote) {
        const reportDataClone = _.cloneDeep(reportData);
        _.assign(reportDataClone.study.facility, {
          facilityNote,
        });
        setReportData(reportDataClone);
      }
      setFindingChanged(false as any); // pre-existing bug: findingsChangeState is typed atom<String>, fed a boolean
    } catch (error) {
      logError('Failed to update summary report: ', error);
      toastrError('Failed to save report', error);
    }
    loadingPageAction();
  };

  /**
   * Reports whether the PDF report tab holds unsaved edits. Always answers false, since findings and facility note are persisted through explicit save and generate actions rather than tracked as dirty state.
   *
   * @returns False, as the tab never reports pending changes.
   */
  const checkChangedData = () => false;

  useImperativeHandle(ref, () => ({
    /**
     * Answers the parent tab container's dirty check for the PDF report tab. Combines the local change check, which never reports edits, with the isNotEdit prop so a read-only tab also stays clean.
     *
     * @returns Whether the tab has unsaved changes and is editable.
     */
    isChangedData() {
      return checkChangedData() && !isNotEdit;
    },
    onClickSaveReport: () => {
      savePdfReportTab();
    },
    resetData() {},
  }));

  /**
   * Uploads a user-dropped PDF file as the study's report and refreshes the viewer with it. Uploads the first accepted file through uploadFileToUrl, registers it with handleUploadAiReport for the profile, toasts the outcome, re-downloads the PDF on success and always clears the page loader.
   *
   * @param acceptedFiles - Files accepted by the dropzone, of which only the first is uploaded.
   * @returns A promise resolving once the upload has finished and the loader has been cleared.
   */
  const handleOnDropFile = async (acceptedFiles: File[]) => {
    if (acceptedFiles.length) {
      loadingPageAction(LOADING_TYPES.UPLOADING_REPORT);
      try {
        const reportUrl = await uploadFileToUrl([acceptedFiles[0]], 'pdf');
        const uploadReportVariables = {
          profileId,
          reportUrl,
        };
        await handleUploadAiReport(uploadReportVariables);
        console.log('Uploaded PDF report successfully');
        toastrSuccess('Upload PDF Report Success', 'Success');
        downloadPdfReport();
      } catch (error) {
        console.log('Failed to upload PDF report: ', error);
        toastrError('Failed to upload PDF Report', 'Failed');
      } finally {
        loadingPageAction();
      }
    }
  };

  useEffect(() => {
    if (
      !_.isEmpty(reportData) &&
      !state.pdfReport?.objectUrl &&
      (reportData.report?.status === 'Generated' ||
        reportData.report?.status === 'Ready' ||
        (reportData.report?.status === 'Dismissed' &&
          reportData.report?.type === REPORT_TYPES.INTERIM))
    ) {
      downloadPdfReport();
    }
  }, []);

  useUpdateEffect(() => {
    const setStateObj = {};
    if (
      (reportData.report?.status === 'Generated' ||
        reportData.report?.status === 'Ready' ||
        (reportData.report?.status === 'Dismissed' &&
          reportData.report?.type === REPORT_TYPES.INTERIM)) &&
      !state.pdfReport?.objectUrl &&
      !state.isDownloadingPdfReport
    ) {
      downloadPdfReport();
    } else if (reportData.report?.status === 'Rendering' || reportData.report?.status === 'None') {
      _.assign(setStateObj, {
        pdfReport: {
          url: '',
          objectUrl: '',
        },
      });
    }
    setState(setStateObj);
  }, [reportData]);

  useEffect(() => {
    try {
      if (reportData.report?.status === 'Rendering') {
        loadingPageAction(LOADING_TYPES.GENERATE_REPORT);
        timerRenderReport.current = setTimeout(() => {
          setState({ isOpenTimeoutReportModal: true });
          setIsDownloadingPdfReport(false);
          loadingPageAction();
        }, TIMEOUT_RENDER_REPORT);
      } else if (
        reportData.report?.status === 'None' &&
        reportData.report?.from !== CALL_CENTER_API
      ) {
        setState({ isOpenTimeoutReportModal: true });
        setIsDownloadingPdfReport(false);
        loadingPageAction();
        if (timerRenderReport.current) {
          clearTimeout(timerRenderReport.current);
          timerRenderReport.current = null;
        }
      } else {
        loadingPageAction();
        setState({ isOpenTimeoutReportModal: false });
        if (timerRenderReport.current) {
          clearTimeout(timerRenderReport.current);
          timerRenderReport.current = null;
        }
      }
    } catch (error) {
      console.log(error);
      timerRenderReport.current = null;
    }
    return () => {
      if (timerRenderReport.current) {
        clearTimeout(timerRenderReport.current);
        timerRenderReport.current = null;
      }
    };
  }, [reportData]);

  /**
   * Decides whether the PDF report tab may be entered while AI processing requests for this study are still outstanding. Reads the pending requestId list from localStorage, short-circuits to active when it is empty or already flagged done, otherwise polls processing status for each stored requestId, drops the finished ones, writes the trimmed list and ai_process flag back, and marks the tab inactive on error.
   *
   * @returns A promise resolving once the tab active flag reflects the outstanding AI requests.
   */
  const checkCanSwitchTab = async () => {
    try {
      const keyStorage = `requestId-${profileId}-${studyFid}`;
      const storedResult = localStorage.getItem(keyStorage);
      const requestData: any[] = JSON.parse(storedResult as string);
      if (_.isEmpty(requestData)) return setIsTabActive(true);
      const { type, requestId } = requestData?.[0] || {};
      if (!requestId || type === keyRecoil) return setIsTabActive(true);
      const isProcessing = localStorage.getItem(`ai_process-${profileId}`);
      if (JSON.parse(isProcessing)) return setIsTabActive(true);
      const promisses: Promise<any>[] = [];
      setIsTabActive(false);
      _.forEach(requestData, (x) => {
        if (x.requestId) {
          promisses.push(fetchAIProcessingStatus(x.requestId, 'switchTab'));
        }
      });
      const result = await Promise.all(promisses);
      let canSwitchTab = false;
      const updateSuccess: any[] = [];
      _.forEach(result, (x, i) => {
        if (x === true) {
          canSwitchTab = true;
          updateSuccess.push(requestData?.[i]);
        }
      });
      _.remove(requestData, (x) => !!_.find(updateSuccess, (y) => y.requestId === x.requestId));
      localStorage.setItem(keyStorage, JSON.stringify(requestData));

      if (canSwitchTab) {
        localStorage.setItem(`ai_process-${profileId}`, JSON.stringify(canSwitchTab));
      }

      setIsTabActive(canSwitchTab);
    } catch (error) {
      console.log(error);
      setIsTabActive(false);
    }
    return null;
  };

  useEffect(() => {
    if (isActiveTab) {
      checkCanSwitchTab();
    }
  }, [isActiveTab]);

  useEmitter(
    EMITTER_CONSTANTS.AI_STOP_PROCESSING,
    async (msg: any) => {
      await clearApolloCache();
      if (isActiveTab && !_.isEmpty(msg)) {
        const { requestId } = msg || {};
        const keyStorage = `requestId-${profileId}-${studyFid}`;
        const storedResult = localStorage.getItem(keyStorage);
        const requestData: any[] = JSON.parse(storedResult as string);
        _.remove(requestData, (x) => x.requestId === requestId);
        localStorage.setItem(keyStorage, JSON.stringify(requestData));
        if (_.isEmpty(requestData)) {
          localStorage.setItem(`ai_process-${profileId}`, JSON.stringify(true));
          setIsTabActive(true);
        }
      }
    },
    [isActiveTab, profileId, studyFid],
  );

  useEffect(() => {
    setIsActiveTab(activeTab === TAB_KEY.PDF_REPORT);
    if (activeTab === TAB_KEY.PDF_REPORT && !hasVisitTab.current) {
      hasVisitTab.current = true;
    }
  }, [activeTab]);

  useEffect(() => {
    if (isGeneratingNormalReportInCallCenter) {
      setState({ isOpenCopyReportModal: false });
    }
  }, [isGeneratingNormalReportInCallCenter]);

  useEmitter(
    EMITTER_CONSTANTS.COMMAND_PROGRESS_UPDATED,
    (msg: any) => {
      const { command, percentage } = msg;
      if (command === 'generate-report-comments') {
        loadingPageAction(`${LOADING_TYPES.GENERATE_FINDING} ${percentage}%`);
      }
    },
    [],
  );

  useEmitter(
    EMITTER_CONSTANTS.ENABLE_UPDATE_FINDINGS,
    () => {
      setIsFindingsDisabled(false);
    },
    [],
  );

  useEmitter(
    EMITTER_CONSTANTS.TECHNICIAN_COMMENT_UPDATED,
    (msg: any) => {
      loadingPageAction();
      setFindings(msg.technicianComments);
      if (isGenerateFindings.current) {
        isGenerateFindings.current = false;
        const currentFindings =
          reportData?.report?.technicianComments || reportData?.holterProfile?.technicianComments;
        setFindingChanged((msg.technicianComments !== currentFindings) as any); // pre-existing bug: findingsChangeState is typed atom<String>, fed a boolean
      }
    },
    [reportData],
  );

  // re download pdf report when report is generated with isUpload flag
  useEmitter(
    EMITTER_CONSTANTS.REPORT_UPDATED,
    async (msg: any) => {
      if (msg.id === reportData?.report?.id && msg.isNonPhi === reportData?.study?.isNonPhi) {
        if (msg.status === 'Generated') {
          downloadPdfReport();
        }
      }
    },
    [reportData],
  );

  const reportStatus = reportData?.report?.status;
  const isDismissedInterimReport =
    reportStatus === 'Dismissed' && reportData?.report?.type === 'Interim';
  const isInterimReport = reportData?.report.type === 'Interim';
  const isReportSent = reportStatus === 'Ready' && reportData?.report?.inbox?.date;
  const isSentInterimReport =
    reportData?.report.type === 'Interim' && !_.isNil(reportData?.report?.inbox?.date);
  const isDisabledDownloadFile =
    !(reportStatus === 'Generated' || reportStatus === 'Ready' || isDismissedInterimReport) ||
    reportData?.report?.isArtifactReport ||
    state.isDownloadingPdfReport;
  const isDisabledCopyReport =
    !(reportStatus === 'Generated' || reportStatus === 'Ready' || isDismissedInterimReport) ||
    isNotEdit ||
    state.isDownloadingPdfReport;

  const isDisabledGenerateDefault =
    isNotEdit ||
    isReportSent ||
    state.isDownloadingPdfReport ||
    isDismissedInterimReport ||
    isAbortedStudy ||
    reportData?.report?.isArtifactReport ||
    isSentInterimReport;

  const isDisabledGenerateReport = isDisabledGenerateDefault || reportStatus === 'Rendering';

  return {
    reportData,
    isShowTabHeader,
    facilityNote,
    findings,
    state,
    setState,
    onChange,
    isFacilityNoteDisabled,
    isTabActive,
    isFailedDownloadReport,
    dropzoneRef,
    reportSettingRef,
    handleGenerateReport,
    handleGenerateReportWhenError,
    toggleUpdateFindingsModal,
    hanldeCloseTimeoutReportModal,
    handleUpdateFinding,
    handleCopyReport,
    handleClickDownloadPdfReport,
    handleOnDropFile,
    isInterimReport,
    isDismissedInterimReport,
    isSentInterimReport,
    isDisabledDownloadFile,
    isDisabledCopyReport,
    isDisabledGenerateDefault,
    isDisabledGenerateReport,
    reportStatus,
  };
};

export default useAiPDFReportTab;
